import type {
  Dialect,
  Diagnostic,
  DomainObject,
  LossReport,
  ModelDocument,
  Relationship,
  TypeSpec,
} from "../shared/contracts.js";
import {
  createModel,
  createObject,
  uid,
  syncDocument,
  validateModel,
  getSelectedPrimaryKeys,
} from "../core/model.js";
import {
  tokenize,
  splitStatements,
  splitTopLevel,
  balanced,
  keyword,
  raw,
  type Token,
  type Statement,
} from "./tokenizer.js";
const emptyLoss = (): LossReport => ({
  preserved: [],
  translated: [],
  preservedOpaque: [],
  lost: [],
  unresolved: [],
});
interface NamePart {
  name: string;
  quoted: boolean;
}
interface Eng {
  dialect?: Dialect;
  identifier?: NamePart[];
  typeDialect?: Dialect;
  columnClause?: string;
  checks?: string[];
  indexExpressions?: string[];
  indexSuffix?: string;
  sequenceOptions?: string;
  opaqueClauses?: string[];
  [key: string]: unknown;
}
interface ConstraintState {
  enabled?: boolean;
  validated?: boolean;
  deferrable?: boolean;
  initiallyDeferred?: boolean;
  match?: string;
}
/** Only recognized state tokens become semantics; storage/version clauses remain source-only. */
function constraintOptions(tokens: Token[]): { state: ConstraintState; rest: Token[] } {
  const state: ConstraintState = {}, rest: Token[] = [];
  for (let i = 0; i < tokens.length;) {
    const t = tokens[i], next = tokens[i + 1];
    if (keyword(t, "ENABLE") || keyword(t, "DISABLE")) { state.enabled = keyword(t, "ENABLE"); i++; }
    else if (keyword(t, "VALIDATE") || keyword(t, "NOVALIDATE")) { state.validated = keyword(t, "VALIDATE"); i++; }
    else if (keyword(t, "NOT") && keyword(next, "VALID")) { state.validated = false; i += 2; }
    else if (keyword(t, "NOT") && keyword(next, "DEFERRABLE")) { state.deferrable = false; i += 2; }
    else if (keyword(t, "DEFERRABLE")) { state.deferrable = true; i++; }
    else if (keyword(t, "INITIALLY") && ["DEFERRED", "IMMEDIATE"].some(w => keyword(next, w))) { state.initiallyDeferred = keyword(next, "DEFERRED"); i += 2; }
    else if (keyword(t, "MATCH") && ["FULL", "SIMPLE", "PARTIAL"].some(w => keyword(next, w))) { state.match = next.value.toUpperCase(); i += 2; }
    else { rest.push(t); i++; }
  }
  return { state, rest };
}
function stateTokenEnd(tokens: Token[], at: number): number {
  let i = at;
  while (i < tokens.length) {
    if (["ENABLE", "DISABLE", "VALIDATE", "NOVALIDATE", "DEFERRABLE"].some(w => keyword(tokens[i], w))) i++;
    else if ((keyword(tokens[i], "NOT") && ["DEFERRABLE", "VALID"].some(w => keyword(tokens[i + 1], w))) || (keyword(tokens[i], "INITIALLY") && ["DEFERRED", "IMMEDIATE"].some(w => keyword(tokens[i + 1], w)))) i += 2;
    else break;
  }
  return i;
}
export const engineering = (o: DomainObject): Eng =>
  (o.extensions.engineering ?? {}) as Eng;
const setEngineering = (o: DomainObject, data: Eng) => {
  o.extensions.engineering = { ...engineering(o), ...data };
};
export function quoteIdentifier(name: string, dialect: Dialect): string {
  if (name.includes("\0")) throw new Error("Identifier contains NUL.");
  return dialect === "mysql"
    ? "`" + name.replaceAll("`", "``") + "`"
    : dialect === "sqlserver" || dialect === "ase"
      ? "[" + name.replaceAll("]", "]]") + "]"
      : '"' + name.replaceAll('"', '""') + '"';
}
export const qualified = (o: DomainObject, d: Dialect) =>
  [
    ...(engineering(o)
      .identifier?.slice(0, -2)
      .map((p) => p.name) ?? []),
    ...(o.schema !== undefined ? [o.schema] : []),
    o.name,
  ]
    .map((n) => n ? quoteIdentifier(n, d) : "")
    .join(".");
function readName(
  tokens: Token[],
  at = 0,
): { parts: NamePart[]; next: number } | undefined {
  const parts: NamePart[] = [];
  let i = at;
  while (
    tokens[i] &&
    (tokens[i].kind === "word" || tokens[i].kind === "identifier")
  ) {
    parts.push({ name: tokens[i].value, quoted: !!tokens[i].quoted });
    i++;
    if (tokens[i]?.text !== ".") break;
    if (tokens[i + 1]?.text === ".") { parts.push({ name: "", quoted: false }); i++; }
    i++;
  }
  return parts.length && tokens[i - 1]?.text !== "."
    ? { parts, next: i }
    : undefined;
}
const nameKey = (parts: NamePart[], d: Dialect) =>
  parts
    .map((p) =>
      p.quoted && ["postgresql", "oracle"].includes(d)
        ? p.name
        : d === "oracle"
          ? p.name.toUpperCase()
          : p.name.toLowerCase(),
    )
    .join("\u0000");
const normalizePart = (p: NamePart, d: Dialect) =>
  p.quoted
    ? p.name
    : d === "postgresql"
      ? p.name.toLowerCase()
      : d === "oracle"
        ? p.name.toUpperCase()
        : p.name;
function nameObject(
  kind: DomainObject["kind"],
  parts: NamePart[],
  rep: string,
  d: Dialect,
  owner?: string,
): DomainObject {
  const normalized = parts.map((p) => ({ ...p, name: normalizePart(p, d) }));
  const o = createObject(kind, normalized.at(-1)!.name, rep, owner);
  if (parts.length > 1) o.schema = normalized.at(-2)!.name;
  setEngineering(o, {
    dialect: d,
    identifier: normalized,
    quotedIdentifier: !!normalized.at(-1)?.quoted,
  });
  return o;
}
function typeSpec(tokens: Token[], source: string): TypeSpec {
  const text = raw(tokens, source);
  const open = tokens.findIndex((t) => t.text === "(");
  const args = open >= 0 ? balanced(tokens, open) : undefined;
  const type: TypeSpec = {
    name: (
      raw(open >= 0 ? tokens.slice(0, open) : tokens, source) +
      (args && args.next < tokens.length
        ? " " + raw(tokens.slice(args.next), source)
        : "")
    ).trim(),
    raw: text,
  };
  if (args) {
    const values = splitTopLevel(args.inside).map((p) =>
      Number(raw(p, source)),
    );
    if (values.every((v) => Number.isSafeInteger(v)) && values[0] > 0) {
      if (/decimal|numeric|number/i.test(type.name)) {
        type.precision = values[0];
        if (values.length > 1) type.scale = values[1];
      } else if (values.length === 1) type.length = values[0];
    } else if (args.inside.length === 2 && args.inside[0].kind === "number" && /^(CHAR|BYTE)$/i.test(args.inside[1].value)) {
      type.length = Number(args.inside[0].value);
    }
  }
  return type;
}
function boundedIndexOptions(tokens: Token[], dialect: Dialect): boolean {
  let i = 0;
  if (keyword(tokens[i], "WITH")) {
    i++;
    const body = balanced(tokens, i);
    const options = body ? body.inside : tokens.slice(i, tokens.findIndex((t, at) => at >= i && keyword(t, "ON")) < 0 ? tokens.length : tokens.findIndex((t, at) => at >= i && keyword(t, "ON")));
    if (!options.length || splitTopLevel(options).some(part => part.length !== 3 || part[0].kind !== "word" || part[1].text !== "=" || !["word", "number", "string"].includes(part[2].kind))) return false;
    i = body ? body.next : i + options.length;
  }
  if (keyword(tokens[i], "TABLESPACE") || ((dialect === "sqlserver" || dialect === "ase") && keyword(tokens[i], "ON"))) {
    const name = readName(tokens, i + 1); if (!name || name.parts.length !== 1) return false; i = name.next;
  }
  if (dialect === "mysql" && keyword(tokens[i], "USING") && ["BTREE", "HASH"].some(w => keyword(tokens[i + 1], w))) i += 2;
  if (dialect === "mysql" && ["VISIBLE", "INVISIBLE"].some(w => keyword(tokens[i], w))) i++;
  return i === tokens.length;
}
function expressionSafe(value: string, dialect: Dialect): boolean {
  const t = tokenize(value, dialect);
  if (
    t.diagnostics.some((d) => d.severity === "error") ||
    t.tokens.some((t) => t.text === ";" || t.kind === "directive")
  )
    return false;
  let depth = 0;
  for (const token of t.tokens) {
    if (token.text === "(") depth++;
    if (token.text === ")" && --depth < 0) return false;
  }
  return depth === 0;
}
export function importDDL(
  sql: string,
  dialect: Dialect,
): {
  document: ModelDocument;
  diagnostics: Diagnostic[];
  lossReport: LossReport;
} {
  const document = createModel(
      "Imported schema",
      "physical",
      "relational",
      dialect,
    ),
    rep = document.model.activeRepresentation,
    lossReport = emptyLoss();
  const parsed = splitStatements(sql, dialect),
    diagnostics = [...parsed.diagnostics];
  const tables = new Map<string, DomainObject>(),
    columnPending: {
      table: DomainObject;
      tokens: Token[];
      statement: Statement;
    }[] = [],
    constraintPending: {
      table: DomainObject;
      tokens: Token[];
      statement: Statement;
    }[] = [],
    others: Statement[] = [];
  const columnLookup = new Map<string, Map<string, DomainObject>>();
  const opaque: { text: string; line: number; reason: string }[] = [];
  let diagnosticStatement: Statement | undefined;
  const issue = (
    code: string,
    message: string,
    line?: number,
    objectId?: string,
    severity: Diagnostic["severity"] = "warning",
  ) =>
    diagnostics.push({
      code,
      severity,
      message,
      line,
      objectId,
      helpTopic: "ddl-import",
      ...(line !== undefined && diagnosticStatement ? { sourceStart: diagnosticStatement.start, sourceEnd: diagnosticStatement.end } : {}),
    });
  const retain = (statement: Statement, reason: string) => {
    opaque.push({ text: statement.text, line: statement.line, reason });
    lossReport.preservedOpaque.push(`Line ${statement.line}: ${reason}`);
    issue("DDL_UNSUPPORTED_CONTENT", reason, statement.line);
    Object.assign(diagnostics.at(-1)!, { sourceStart: statement.start, sourceEnd: statement.end });
  };
  const findTable = (parts: NamePart[]) => {
    let table = tables.get(nameKey(parts, dialect));
    if (!table && parts.length === 1) {
      const matches = [...tables.values()].filter(
        (o) =>
          nameKey(
            [
              {
                name: o.name,
                quoted: !!engineering(o).identifier?.at(-1)?.quoted,
              },
            ],
            dialect,
          ) === nameKey(parts, dialect),
      );
      if (matches.length === 1) table = matches[0];
    }
    return table;
  };
  const findColumn = (table: DomainObject, name: NamePart) =>
    columnLookup.get(table.objectId)?.get(nameKey([name], dialect));
  const applyOptions = (object: DomainObject, tokens: Token[], statement: Statement) => {
    setEngineering(object, { sourceLine: statement.line, sourceStart: statement.start, sourceEnd: statement.end });
    const { state, rest } = constraintOptions(tokens);
    if (dialect === "sqlserver") {
      const at = statement.tokens.findIndex(t => keyword(t, "WITH"));
      if (at >= 0 && ["CHECK", "NOCHECK"].some(w => keyword(statement.tokens[at + 1], w))) { state.enabled = true; state.validated = keyword(statement.tokens[at + 1], "CHECK"); }
    }
    if (Object.keys(state).length) setEngineering(object, { constraintState: state, dialect });
    if (rest.length) {
      const clause = raw(rest, sql); setEngineering(object, { opaqueClauses: [clause] });
      lossReport.preservedOpaque.push(`${object.name}: ${clause}`);
      issue("DDL_CONSTRAINT_OPTIONS_OPAQUE", `Constraint property is source-only and omitted from generation: ${clause}`, statement.line, object.objectId);
    }
  };
  const members = (table: DomainObject, tokens: Token[], line: number) => {
    const ids: string[] = [];
    for (const part of splitTopLevel(tokens)) {
      const n = readName(part);
      const field =
        n?.parts.length === 1 && (n.next === part.length || (n.next === part.length - 1 && ["ASC", "DESC"].some(w => keyword(part[n.next], w))))
          ? findColumn(table, n.parts[0])
          : undefined;
      if (!field) {
        issue(
          "DDL_UNRESOLVED_COLUMN",
          `Cannot bind key member ${raw(part, sql)} in ${table.name}.`,
          line,
          table.objectId,
          "error",
        );
        return undefined;
      }
      ids.push(field.objectId);
    }
    return ids;
  };
  let activeSchema: NamePart | undefined;
  const statementSchemas = new Map<Statement, NamePart | undefined>();
  const scopedName = (parts: NamePart[], statement: Statement) => parts.length === 1 && statementSchemas.get(statement) ? [statementSchemas.get(statement)!, ...parts] : parts;
  // First establish table identities, then columns, then ordered constraints and external references.
  for (const statement of parsed.statements) {
    diagnosticStatement = statement;
    const t = statement.tokens;
    if (dialect === "postgresql" && keyword(t[0], "SET") && keyword(t[1], "SEARCH_PATH")) {
      const candidate = readName(t, 3); activeSchema = candidate?.parts[0];
      statementSchemas.set(statement, activeSchema); continue;
    }
    if (dialect === "postgresql" && keyword(t[0], "RESET") && keyword(t[1], "SEARCH_PATH")) { activeSchema = undefined; continue; }
    statementSchemas.set(statement, activeSchema);
    let tableAt = -1;
    if (keyword(t[0], "CREATE")) {
      for (let i = 1; i < Math.min(t.length, 5); i++) {
        if (keyword(t[i], "TABLE")) {
          tableAt = i;
          break;
        }
        if (
          !["GLOBAL", "LOCAL", "TEMPORARY", "TEMP", "UNLOGGED"].includes(
            t[i].value.toUpperCase(),
          )
        )
          break;
      }
    }
    if (tableAt < 0) {
      others.push(statement);
      continue;
    }
    let i = tableAt + 1;
    if (
      keyword(t[i], "IF") &&
      keyword(t[i + 1], "NOT") &&
      keyword(t[i + 2], "EXISTS")
    )
      i += 3;
    const n = readName(t, i),
      body = n && balanced(t, n.next);
    if (!n || !body) {
      retain(
        statement,
        "CREATE TABLE form without a supported parenthesized definition.",
      );
      continue;
    }
    n.parts = scopedName(n.parts, statement);
    const key = nameKey(n.parts, dialect);
    if (tables.has(key)) {
      issue(
        "DDL_DUPLICATE_OBJECT",
        `Duplicate table ${n.parts.map((p) => p.name).join(".")}.`,
        statement.line,
        undefined,
        "error",
      );
      retain(
        statement,
        "Duplicate table definition retained; no last-writer overwrite.",
      );
      continue;
    }
    const table = nameObject("table", n.parts, rep, dialect);
    setEngineering(table, { sourceLine: statement.line, sourceStart: statement.start, sourceEnd: statement.end });
    tables.set(key, table);
    document.objects.push(table);
    for (const entry of splitTopLevel(body.inside)) {
      if (
        ["CONSTRAINT", "PRIMARY", "UNIQUE", "FOREIGN", "CHECK", "KEY", "INDEX", "FULLTEXT", "SPATIAL"].some((w) =>
          keyword(entry[0], w),
        )
      )
        constraintPending.push({ table, tokens: entry, statement });
      else columnPending.push({ table, tokens: entry, statement });
    }
    if (tableAt > 1 || body.next < t.length) {
      const clauses = [
        raw(t.slice(1, tableAt), sql),
        raw(t.slice(body.next), sql),
      ].filter(Boolean);
      setEngineering(table, { opaqueClauses: clauses });
      for (const clause of clauses) {
        lossReport.preservedOpaque.push(`${table.name}: ${clause}`);
        issue(
          "DDL_TABLE_OPTIONS_OPAQUE",
          `Table option is preserved but not generated: ${clause}`,
          statement.line,
          table.objectId,
        );
      }
    }
    lossReport.preserved.push(`Table ${qualified(table, dialect)}`);
  }
  // ALTER TABLE ADD is deliberately supported; other schema evolution is not simulated.
  for (const statement of others.slice()) {
    diagnosticStatement = statement;
    const t = statement.tokens;
    if (!keyword(t[0], "ALTER") || !keyword(t[1], "TABLE")) continue;
    const n = readName(t, keyword(t[2], "ONLY") ? 3 : 2),
      table = n && findTable(scopedName(n.parts, statement));
    if (n && table && keyword(t[n.next], "ALTER") && keyword(t[n.next + 1], "COLUMN")) continue;
    let addAt = n?.next ?? 0;
    if (keyword(t[addAt], "WITH") && ["CHECK", "NOCHECK"].some(w => keyword(t[addAt + 1], w))) addAt += 2;
    if (n && table && ["CHECK", "NOCHECK"].some(w => keyword(t[addAt], w)) && keyword(t[addAt + 1], "CONSTRAINT")) continue;
    if (!n || !table || !keyword(t[addAt], "ADD")) {
      retain(
        statement,
        "ALTER form or target is unresolved; final-state evolution is not assumed.",
      );
      continue;
    }
    let tail = t.slice(addAt + 1);
    if (keyword(tail[0], "COLUMN")) tail = tail.slice(1);
    const wrapped = balanced(tail, 0);
    const entries =
      wrapped && wrapped.next === tail.length
        ? splitTopLevel(wrapped.inside)
        : splitTopLevel(tail);
    for (const tokens of entries) {
      if (
        ["CONSTRAINT", "PRIMARY", "UNIQUE", "FOREIGN", "CHECK", "DEFAULT"].some((w) =>
          keyword(tokens[0], w),
        )
      )
        constraintPending.push({ table, tokens, statement });
      else columnPending.push({ table, tokens, statement });
    }
  }
  const sourceOnlyTypes = new Map<string, string>();
  for (const statement of parsed.statements) {
    const tokens = statement.tokens;
    const name = keyword(tokens[0], "CREATE") && ["TYPE", "DOMAIN"].some(w => keyword(tokens[1], w)) ? readName(tokens, 2)
      : ["EXEC", "EXECUTE"].some(w => keyword(tokens[0], w)) && keyword(tokens[1], "SP_ADDTYPE") ? readName(tokens, 2) : undefined;
    if (name) sourceOnlyTypes.set(nameKey(scopedName(name.parts, statement), dialect), raw(name.parts.length ? tokens.slice(name.next - name.parts.length * 2 + 1, name.next) : [], sql) || name.parts.map(p => p.name).join("."));
  }
  for (const { table, tokens: t, statement } of columnPending) {
    diagnosticStatement = statement;
    const n = readName(t);
    if (!n || n.parts.length !== 1) {
      retain(statement, `Invalid column definition in ${table.name}.`);
      continue;
    }
    if (findColumn(table, n.parts[0])) {
      issue(
        "DDL_DUPLICATE_COLUMN",
        `Duplicate column ${n.parts[0].name}.`,
        t[0].line,
        table.objectId,
        "error",
      );
      continue;
    }
    let end = n.next,
      depth = 0;
    for (; end < t.length; end++) {
      if (t[end].text === "(") depth++;
      if (t[end].text === ")") depth--;
      if (
        depth === 0 &&
        [
          "NOT",
          "NULL",
          "DEFAULT",
          "PRIMARY",
          "UNIQUE",
          "REFERENCES",
          "CHECK",
          "CONSTRAINT",
          "GENERATED",
          "AS",
          "COMPUTE",
          "IDENTITY",
          "AUTO_INCREMENT",
          "COLLATE",
        ].some((w) => keyword(t[end], w))
      )
        break;
    }
    const inferredType = end === n.next && ["GENERATED", "AS", "COMPUTE"].some(w => keyword(t[end], w));
    if (end === n.next && !inferredType) {
      issue(
        "DDL_TYPE_REQUIRED",
        `Missing datatype for ${n.parts[0].name}.`,
        t[0].line,
        table.objectId,
        "error",
      );
      continue;
    }
    const col = nameObject("column", n.parts, rep, dialect, table.objectId);
    if (!inferredType) col.type = typeSpec(t.slice(n.next, end), sql);
    if (inferredType) setEngineering(col, { inferredType: true });
    if (dialect === "postgresql" && /^(smallserial|serial|bigserial|serial2|serial4|serial8)$/i.test(col.type?.name ?? "")) { col.generated = true; col.nullable = false; setEngineering(col, { generationKind: "serial" }); }
    const typeName = readName(t.slice(n.next, end));
    const unresolvedType = (typeName && sourceOnlyTypes.get(nameKey(scopedName(typeName.parts, statement), dialect))) || (dialect === "mysql" && /^(ENUM|SET)$/i.test(col.type?.name ?? "") ? col.type!.name : undefined);
    if (unresolvedType) {
      setEngineering(col, { sourceOnlyType: unresolvedType });
      issue("DDL_TYPE_SOURCE_ONLY", `Datatype ${unresolvedType} is declared in source-only content. Its domain/enum/usertype semantics are not modeled; dependent columns cannot be faithfully generated.`, t[0].line, col.objectId);
      lossReport.unresolved.push(`${table.name}.${col.name}: source-only datatype ${unresolvedType}`);
    }
    col.ordinal = columnLookup.get(table.objectId)?.size ?? 0;
    setEngineering(col, {
      typeDialect: dialect,
      sourceLine: t[0].line, sourceStart: t[0].start, sourceEnd: t.at(-1)!.end,
      originalType: structuredClone(col.type),
      typeLengthSemantics: /\(\s*\d+\s+(CHAR|BYTE)\s*\)/i.exec(col.type?.raw ?? "")?.[1].toUpperCase(),
    });
    document.objects.push(col);
    if (!columnLookup.has(table.objectId))
      columnLookup.set(table.objectId, new Map());
    columnLookup.get(table.objectId)!.set(nameKey(n.parts, dialect), col);
    let constraintName: string | undefined;
    for (let i = end; i < t.length;) {
      if (keyword(t[i], "NOT") && keyword(t[i + 1], "NULL")) {
        col.nullable = false;
        setEngineering(col, { explicitNotNull: true });
        if (constraintName) {
          setEngineering(col, {
            opaqueClauses: [
              ...(engineering(col).opaqueClauses ?? []),
              "Named NOT NULL constraint " + constraintName,
            ],
          });
          lossReport.preservedOpaque.push(
            `${col.name}: NOT NULL constraint name ${constraintName}`,
          );
          constraintName = undefined;
        }
        i += 2;
        continue;
      }
      if (keyword(t[i], "NULL")) {
        col.nullable = true;
        i++;
        continue;
      }
      if (keyword(t[i], "CONSTRAINT")) {
        constraintName = t[i + 1] && normalizePart({ name: t[i + 1].value, quoted: !!t[i + 1].quoted }, dialect);
        i += 2;
        continue;
      }
      if (
        (keyword(t[i], "PRIMARY") && keyword(t[i + 1], "KEY")) ||
        keyword(t[i], "UNIQUE")
      ) {
        const primary = keyword(t[i], "PRIMARY");
        const key = createObject(
          "key",
          constraintName ??
            `${table.name}_${col.name}_${primary ? "pk" : "uq"}`,
          rep,
          table.objectId,
        );
        key.members = [col.objectId];
        key.keyRole = primary ? "primary" : "unique";
        key.selectedPrimary = primary;
        key.unique = true;
        let afterKey = i + (primary ? 2 : 1);
        const clustering = ["CLUSTERED", "NONCLUSTERED"].find(w => keyword(t[afterKey], w));
        if (clustering) { setEngineering(key, { dialect, clustering }); afterKey++; }
        const stateEnd = stateTokenEnd(t, afterKey); applyOptions(key, t.slice(afterKey, stateEnd), statement);
        if (primary && (engineering(key).constraintState as ConstraintState | undefined)?.enabled !== false) col.nullable = false;
        document.objects.push(key);
        constraintName = undefined;
        i = stateEnd;
        continue;
      }
      if (keyword(t[i], "DEFAULT")) {
        const defaultOnNull = dialect === "oracle" && keyword(t[i + 1], "ON") && keyword(t[i + 2], "NULL");
        const valueAt = i + (defaultOnNull ? 3 : 1);
        if (defaultOnNull) { col.nullable = false; setEngineering(col, { defaultOnNull: true }); }
        let j = valueAt,
          d = 0;
        for (; j < t.length; j++) {
          if (t[j].text === "(") d++;
          if (t[j].text === ")") d--;
          if (
            j > valueAt &&
            d === 0 &&
            [
              "NOT",
              "NULL",
              "PRIMARY",
              "UNIQUE",
              "REFERENCES",
              "CHECK",
              "CONSTRAINT",
              "GENERATED",
              "COLLATE",
            ].some((w) => keyword(t[j], w))
          )
            break;
        }
        col.defaultValue = raw(t.slice(valueAt, j), sql);
        if (!col.defaultValue) issue("DDL_DEFAULT_SYNTAX", "DEFAULT requires a nonempty expression.", t[i].line, col.objectId, "error");
        if (constraintName) {
          setEngineering(col, { defaultConstraintName: constraintName });
          constraintName = undefined;
        }
        i = j;
        continue;
      }
      if (keyword(t[i], "CHECK")) {
        const body = balanced(t, i + 1);
        if (!body || !body.inside.length) {
          issue(
            "DDL_CHECK_SYNTAX",
            "Unbalanced CHECK expression.",
            t[i].line,
            col.objectId,
            "error",
          );
          break;
        }
        const check = createObject(
          "constraint",
          constraintName ?? `${table.name}_${col.name}_check`,
          rep,
          table.objectId,
        );
        check.checkExpression = raw(body.inside, sql);
        setEngineering(check, { dialect });
        document.objects.push(check);
        const stateEnd = stateTokenEnd(t, body.next); applyOptions(check, t.slice(body.next, stateEnd), statement);
        constraintName = undefined;
        i = stateEnd;
        continue;
      }
      if (keyword(t[i], "REFERENCES")) {
        const targetName = readName(t, i + 1),
          targetMembers = targetName && balanced(t, targetName.next);
        let next = targetMembers?.next ?? targetName?.next ?? t.length;
        while (
          keyword(t[next], "ON") &&
          (keyword(t[next + 1], "DELETE") || keyword(t[next + 1], "UPDATE"))
        ) {
          next += 2;
          next += keyword(t[next], "SET") || keyword(t[next], "NO") ? 2 : 1;
        }
        const separate =
          next < t.length &&
          [
            "NOT",
            "NULL",
            "DEFAULT",
            "CHECK",
            "CONSTRAINT",
            "PRIMARY",
            "UNIQUE",
            "GENERATED",
          ].some((w) => keyword(t[next], w));
        const end = separate ? next : t.length;
        const prefix: Token[] = constraintName
          ? [
              {
                ...t[i],
                kind: "word",
                text: "CONSTRAINT",
                value: "CONSTRAINT",
              },
              {
                ...t[i],
                kind: "identifier",
                text: constraintName,
                value: constraintName,
              },
            ]
          : [];
        const synthetic: Token[] = [
          ...prefix,
          { ...t[i], kind: "word", text: "FOREIGN", value: "FOREIGN" },
          { ...t[i], kind: "word", text: "KEY", value: "KEY" },
          { ...t[i], kind: "symbol", text: "(", value: "(" },
          t[0],
          { ...t[i], kind: "symbol", text: ")", value: ")" },
          ...t.slice(i, end),
        ];
        constraintPending.push({ table, tokens: synthetic, statement });
        constraintName = undefined;
        i = end;
        continue;
      }
      if (
        ["GENERATED", "IDENTITY", "AUTO_INCREMENT", "AS", "COMPUTE"].some((w) =>
          keyword(t[i], w),
        )
      ) {
        col.generated = true;
        const identity = keyword(t[i], "IDENTITY") || keyword(t[i], "AUTO_INCREMENT") || t.slice(i).some(token => keyword(token, "IDENTITY"));
        setEngineering(col, { generationKind: identity ? "identity" : "computed" });
        if (identity) col.nullable = false;
        let j = i + 1,
          d = 0;
        for (; j < t.length; j++) {
          if (t[j].text === "(") d++;
          if (t[j].text === ")") d--;
          if (
            d === 0 &&
            !(keyword(t[j], "NULL") && keyword(t[j - 1], "ON")) &&
            !(keyword(t[j], "NOT") && keyword(t[j + 1], "MATERIALIZED")) &&
            [
              "PRIMARY",
              "UNIQUE",
              "REFERENCES",
              "CHECK",
              "CONSTRAINT",
              "NOT",
              "NULL",
            ].some((w) => keyword(t[j], w))
          )
            break;
        }
        const clauseTokens = t.slice(i, j);
        if (!identity) {
          const expressionAt = clauseTokens.findIndex(token => keyword(token, "AS") || keyword(token, "COMPUTE"));
          const first = clauseTokens[expressionAt + 1], body = balanced(clauseTokens, expressionAt + 1);
          if (expressionAt < 0 || !first || (first.text === "(" && !body?.inside.length) || ["STORED", "VIRTUAL", "PERSISTED", "MATERIALIZED"].some(w => keyword(first, w))) issue("DDL_GENERATED_SYNTAX", "Computed/generated columns require a nonempty expression.", t[i].line, col.objectId, "error");
          if (body) {
            const suffix = raw(clauseTokens.slice(body.next), sql).trim().replace(/\s+/g, " ").toUpperCase();
            const supported = dialect === "sqlserver" ? ["", "PERSISTED"] : dialect === "ase" ? ["", "MATERIALIZED", "NOT MATERIALIZED"] : dialect === "oracle" ? ["", "VIRTUAL"] : ["", "STORED", "VIRTUAL"];
            if (!supported.includes(suffix)) issue("DDL_GENERATED_SYNTAX", `Unsupported computed/generated suffix: ${suffix}. Repair or remove it before importing this column.`, t[i].line, col.objectId, "error");
          }
        }
        setEngineering(col, { columnClause: raw(clauseTokens, sql), dialect });
        i = j;
        continue;
      }
      const clause = raw(t.slice(i), sql);
      setEngineering(col, { opaqueClauses: [clause] });
      issue(
        "DDL_COLUMN_OPTION_OPAQUE",
        `Column option preserved but not generated: ${clause}`,
        t[i].line,
        col.objectId,
      );
      lossReport.preservedOpaque.push(`${table.name}.${col.name}: ${clause}`);
      break;
    }
  }
  // Defaults added after CREATE TABLE bind to existing columns without manufacturing constraints.
  constraintPending.sort(
    (a, b) =>
      Number(a.tokens.some((t) => keyword(t, "FOREIGN"))) -
      Number(b.tokens.some((t) => keyword(t, "FOREIGN"))),
  );
  for (const { table, tokens: t, statement } of constraintPending) {
    diagnosticStatement = statement;
    if (dialect === "mysql" && ["KEY", "INDEX", "FULLTEXT", "SPATIAL"].some(w => keyword(t[0], w))) {
      let at = ["FULLTEXT", "SPATIAL"].some(w => keyword(t[0], w)) ? 1 : 0;
      const category = at ? t[0].value.toUpperCase() : undefined;
      if (keyword(t[at], "KEY") || keyword(t[at], "INDEX")) at++;
      const n = readName(t, at); if (n) at = n.next;
      const body = balanced(t, at);
      if (!body) { retain(statement, "Inline index requires a name and ordered members."); continue; }
      const index = nameObject("index", n?.parts ?? [{name: table.name + "_idx_" + document.objects.length, quoted:false}], rep, dialect, table.objectId);
      const parts = splitTopLevel(body.inside).map(p => { const cn = readName(p), col = cn && findColumn(table, cn.parts[0]); return col && cn ? { fieldId: col.objectId, suffix: raw(p.slice(cn.next), sql) } : { expression: raw(p, sql) }; });
      index.members = parts.flatMap(p => p.fieldId ? [p.fieldId] : []); index.expression = raw(body.inside, sql);
      const suffix = t.slice(body.next), indexUnsupportedOptions = !boundedIndexOptions(suffix, dialect);
      if (indexUnsupportedOptions) { issue("DDL_INDEX_OPTIONS_OPAQUE", `Inline index suffix is source-only: ${raw(suffix, sql)}`, statement.line, index.objectId); lossReport.preservedOpaque.push(`${index.name}: ${raw(suffix, sql)}`); }
      setEngineering(index, { dialect, clustering: category, indexParts: parts, indexSuffix: raw(suffix, sql), indexUnsupportedOptions });
      document.objects.push(index); continue;
    }
    let i = 0,
      name: string | undefined;
    if (keyword(t[i], "CONSTRAINT")) {
      name = t[i + 1] && normalizePart({ name: t[i + 1].value, quoted: !!t[i + 1].quoted }, dialect);
      i += 2;
    }
    if (keyword(t[i], "DEFAULT")) {
      const forAt = t.findIndex((token, at) => at > i && keyword(token, "FOR"));
      const cn = forAt >= 0 && readName(t, forAt + 1), col = cn && findColumn(table, cn.parts[0]);
      if (!col || !cn || cn.next !== t.length) { retain(statement, "DEFAULT FOR target is unresolved."); continue; }
      col.defaultValue = raw(t.slice(i + 1, forAt), sql); setEngineering(col, { defaultConstraintName: name }); continue;
    }
    if (keyword(t[i], "CHECK")) {
      const b = balanced(t, i + 1);
      if (!b || !b.inside.length) {
        issue("DDL_CHECK_SYNTAX", "CHECK requires a nonempty balanced expression.", statement.line, table.objectId, "error");
        retain(statement, "Malformed CHECK constraint.");
        continue;
      }
      const check = createObject(
        "constraint",
        name ??
          `${table.name}_check_${document.objects.filter((o) => o.kind === "constraint").length + 1}`,
        rep,
        table.objectId,
      );
      check.checkExpression = raw(b.inside, sql);
      setEngineering(check, { dialect });
      document.objects.push(check);
      applyOptions(check, t.slice(b.next), statement);
      continue;
    }
    const primary = keyword(t[i], "PRIMARY"),
      unique = keyword(t[i], "UNIQUE"),
      foreign = keyword(t[i], "FOREIGN");
    if (!primary && !unique && !foreign) {
      retain(statement, "Unsupported table constraint.");
      continue;
    }
    i += primary || foreign ? 2 : 1;
    const clustering = ["CLUSTERED", "NONCLUSTERED"].find(w => keyword(t[i], w)); if (clustering) i++;
    const nullsNotDistinct = keyword(t[i], "NULLS") && keyword(t[i + 1], "NOT") && keyword(t[i + 2], "DISTINCT"); if (nullsNotDistinct) i += 3;
    if (dialect === "mysql" && (keyword(t[i], "KEY") || keyword(t[i], "INDEX"))) i++;
    if (dialect === "mysql" && t[i]?.text !== "(" && (t[i]?.kind === "word" || t[i]?.kind === "identifier")) { name ??= t[i].value; i++; }
    const b = balanced(t, i);
    if (!b) {
      retain(statement, "Constraint requires ordered parenthesized members.");
      continue;
    }
    const ids = members(table, b.inside, statement.line);
    if (!ids) continue;
    i = b.next;
    if (primary || unique) {
      const key = createObject(
        "key",
        name ??
          `${table.name}_${primary ? "pk" : "uq_" + (document.objects.filter((o) => o.ownerObjectId === table.objectId && o.kind === "key").length + 1)}`,
        rep,
        table.objectId,
      );
      key.members = ids;
      key.keyRole = primary ? "primary" : "unique";
      key.selectedPrimary = primary;
      key.unique = true;
      setEngineering(key, { dialect, clustering, nullsNotDistinct, memberDirections: splitTopLevel(b.inside).map(p => /^(ASC|DESC)$/i.test(p.at(-1)?.value ?? "") ? p.at(-1)!.value.toUpperCase() : "") });
      document.objects.push(key);
      applyOptions(key, t.slice(i), statement);
      if (primary && (engineering(key).constraintState as ConstraintState | undefined)?.enabled !== false)
        for (const id of ids)
          document.objects.find((o) => o.objectId === id)!.nullable = false;
      continue;
    }
    if (!keyword(t[i], "REFERENCES")) {
      retain(statement, "Foreign key has no REFERENCES target.");
      continue;
    }
    const targetName = readName(t, i + 1),
      target = targetName && findTable(scopedName(targetName.parts, statement));
    if (!target || !targetName) {
      issue(
        "DDL_UNRESOLVED_REFERENCE",
        `Unresolved FK target ${raw(t.slice(i + 1), sql)}.`,
        statement.line,
        table.objectId,
        "error",
      );
      retain(statement, "Unresolved foreign key kept as source content.");
      continue;
    }
    const targetBody = balanced(t, targetName.next);
    let targetIds: string[] | undefined;
    let next = targetName.next;
    if (targetBody) {
      targetIds = members(target, targetBody.inside, statement.line);
      next = targetBody.next;
    } else
      targetIds = document.objects.find(
        (o) =>
          o.ownerObjectId === target.objectId &&
          o.kind === "key" &&
          o.keyRole === "primary",
      )?.members;
    if (!targetIds || ids.length !== targetIds.length) {
      issue(
        "DDL_FK_ARITY",
        "Foreign key members are unresolved or have different arity.",
        statement.line,
        table.objectId,
        "error",
      );
      continue;
    }
    if (!document.objects.some(o => o.kind === "key" && o.ownerObjectId === target.objectId && o.members?.length === targetIds!.length && o.members.every((id, at) => dialect === "postgresql" ? targetIds!.includes(id) : id === targetIds![at]))) {
      issue("DDL_FK_TARGET_KEY", "Referenced columns do not match an ordered modeled primary or unique key.", statement.line, table.objectId, "error");
      retain(statement, "Foreign key is unresolved because its referenced key is not modeled."); continue;
    }
    const relationship: Relationship = {
      relationshipId: uid(),
      name:
        name ??
        `${table.name}_${target.name}_fk_${document.relationships.length + 1}`,
      representationId: rep,
      sourceObjectId: table.objectId,
      targetObjectId: target.objectId,
      sourceCardinality: "many",
      targetCardinality: "one",
      sourceOptional: ids.some(
        (id) =>
          document.objects.find((o) => o.objectId === id)?.nullable !== false,
      ),
      targetOptional: true,
      identifying: ids.every((id) =>
        document.objects.some(
          (k) =>
            k.ownerObjectId === table.objectId &&
            k.keyRole === "primary" &&
            k.members?.includes(id),
        ),
      ),
      sourceAttributeIds: ids,
      targetAttributeIds: targetIds,
      extensions: { engineering: { dialect, sourceLine: statement.line, sourceStart: statement.start, sourceEnd: statement.end } },
    };
    for (let j = next; j < t.length;) {
      if (
        keyword(t[j], "ON") &&
        (keyword(t[j + 1], "DELETE") || keyword(t[j + 1], "UPDATE"))
      ) {
        let k = j + 2;
        const first = t[k]?.value.toUpperCase();
        let action = first;
        if (first === "SET" || first === "NO") {
          action += " " + t[++k]?.value.toUpperCase();
        }
        if (
          ![
            "CASCADE",
            "RESTRICT",
            "SET NULL",
            "SET DEFAULT",
            "NO ACTION",
          ].includes(action ?? "")
        ) {
          issue(
            "DDL_FK_ACTION",
            "Unsupported referential action.",
            statement.line,
            relationship.relationshipId,
            "error",
          );
          break;
        }
        if (keyword(t[j + 1], "DELETE")) relationship.onDelete = action;
        else relationship.onUpdate = action;
        j = k + 1;
      } else if (keyword(t[j], "MATCH") && ["FULL", "SIMPLE", "PARTIAL"].some(w => keyword(t[j + 1], w))) {
        (relationship.extensions!.engineering as Eng).constraintState = { ...((relationship.extensions!.engineering as Eng).constraintState as ConstraintState ?? {}), match: t[j + 1].value.toUpperCase() }; j += 2;
      } else {
        const { state, rest } = constraintOptions(t.slice(j));
        const eng = relationship.extensions!.engineering as Eng;
        eng.constraintState = { ...(eng.constraintState as ConstraintState ?? {}), ...state };
        if (!rest.length) break;
        relationship.extensions!.opaqueClauses = raw(rest, sql);
        lossReport.preservedOpaque.push(
          `${relationship.name}: ${raw(t.slice(j), sql)}`,
        );
        issue(
          "DDL_FK_OPTIONS_OPAQUE",
          "Foreign-key trailing options preserved only.",
          statement.line,
          relationship.relationshipId,
        );
        break;
      }
    }
    if (dialect === "sqlserver") {
      const at = statement.tokens.findIndex(t => keyword(t, "WITH"));
      if (at >= 0 && ["CHECK", "NOCHECK"].some(w => keyword(statement.tokens[at + 1], w))) (relationship.extensions!.engineering as Eng).constraintState = { enabled: true, validated: keyword(statement.tokens[at + 1], "CHECK") };
    }
    document.relationships.push(relationship);
  }
  for (const statement of others) {
    diagnosticStatement = statement;
    const t = statement.tokens;
    if (keyword(t[0], "ALTER") && keyword(t[1], "TABLE")) {
      const n = readName(t, keyword(t[2], "ONLY") ? 3 : 2), table = n && findTable(scopedName(n.parts, statement));
      if (n && table && keyword(t[n.next], "ALTER") && keyword(t[n.next + 1], "COLUMN")) {
        const cn = readName(t, n.next + 2), col = cn && findColumn(table, cn.parts[0]);
        if (col && cn && keyword(t[cn.next], "SET") && keyword(t[cn.next + 1], "DEFAULT")) col.defaultValue = raw(t.slice(cn.next + 2), sql);
        else retain(statement, "Unsupported ALTER COLUMN evolution; no final-state semantics assumed.");
      }
      let stateAt = n?.next ?? 0;
      const withState = keyword(t[stateAt], "WITH") && ["CHECK", "NOCHECK"].some(w => keyword(t[stateAt + 1], w));
      if (withState) stateAt += 2;
      if (n && table && ["CHECK", "NOCHECK"].some(w => keyword(t[stateAt], w)) && keyword(t[stateAt + 1], "CONSTRAINT")) {
        const names = t.slice(stateAt + 2).filter(token => token.text !== ",").map(token => token.value);
        const entries = [...document.objects.filter(o => o.ownerObjectId === table.objectId && ["key", "constraint"].includes(o.kind)), ...document.relationships.filter(r => r.sourceObjectId === table.objectId)];
        for (const name of names) {
          const matches = entries.filter(o => name.toUpperCase() === "ALL" || o.name.toLowerCase() === name.toLowerCase());
          if (!matches.length) retain(statement, `Constraint state target ${name} is unresolved.`);
          for (const object of matches) {
            object.extensions ??= {}; const eng = object.extensions.engineering as Eng ?? {};
            const previous = eng.constraintState as ConstraintState ?? {};
            eng.constraintState = { ...previous, enabled: keyword(t[stateAt], "CHECK"), ...(!keyword(t[stateAt], "CHECK") ? { validated: false } : withState ? { validated: keyword(t[n!.next + 1], "CHECK") } : {}) };
            object.extensions.engineering = eng;
          }
        }
      }
    }
  }
  for (const statement of others) {
    diagnosticStatement = statement;
    const t = statement.tokens;
    if (keyword(t[0], "ALTER") && keyword(t[1], "TABLE")) continue;
    let indexAt = 1;
    const indexUnique = keyword(t[indexAt], "UNIQUE"); if (indexUnique) indexAt++;
    const indexClustering = ["CLUSTERED", "NONCLUSTERED", "FULLTEXT", "SPATIAL"].find(w => keyword(t[indexAt], w)); if (indexClustering) indexAt++;
    if (keyword(t[0], "CREATE") && keyword(t[indexAt], "INDEX")) {
      const unique = indexUnique, n = readName(t, indexAt + 1);
      if (!n || !keyword(t[n.next], "ON")) {
        retain(statement, "Unsupported CREATE INDEX syntax.");
        continue;
      }
      const targetName = readName(t, n.next + 1),
        table = targetName && findTable(scopedName(targetName.parts, statement));
      const methodAt = targetName?.next ?? 0, indexMethod = keyword(t[methodAt], "USING") ? t[methodAt + 1]?.value : undefined;
      const body = targetName && balanced(t, targetName.next + (indexMethod ? 2 : 0));
      if (!table || !body) {
        retain(statement, "Index target or members are unresolved.");
        continue;
      }
      const index = nameObject("index", n.parts, rep, dialect, table.objectId);
      index.unique = unique;
      index.members = [];
      const expressions = splitTopLevel(body.inside).map((p) => raw(p, sql)),
        indexParts: {
          fieldId?: string;
          suffix?: string;
          expression?: string;
        }[] = [];
      for (const part of splitTopLevel(body.inside)) {
        const cn = readName(part),
          col = cn && findColumn(table, cn.parts[0]);
        if (
          col &&
          cn &&
          cn.parts.length === 1 &&
          (cn.next === part.length ||
            (cn.next === part.length - 1 &&
              ["ASC", "DESC"].some((w) => keyword(part[cn.next], w))))
        ) {
          index.members.push(col.objectId);
          indexParts.push({
            fieldId: col.objectId,
            suffix: raw(part.slice(cn.next), sql),
          });
        } else indexParts.push({ expression: raw(part, sql) });
      }
      index.expression = expressions.join(", ");
      let suffixTokens = t.slice(body.next), indexIncludedFieldIds: string[] | undefined;
      if (keyword(suffixTokens[0], "INCLUDE")) {
        const included = balanced(suffixTokens, 1);
        if (included) { indexIncludedFieldIds = members(table, included.inside, statement.line); suffixTokens = suffixTokens.slice(included.next); }
      }
      const hasPredicate = keyword(suffixTokens[0], "WHERE");
      const indexPredicate = hasPredicate ? raw(suffixTokens.slice(1), sql) : undefined;
      const indexUnsupportedOptions = !hasPredicate && !boundedIndexOptions(suffixTokens, dialect);
      if (hasPredicate && !indexPredicate) issue("DDL_INDEX_PREDICATE", "WHERE requires a nonempty predicate.", statement.line, index.objectId, "error");
      if (indexUnsupportedOptions) { issue("DDL_INDEX_OPTIONS_OPAQUE", `Index suffix is source-only; its semantics cannot be generated: ${raw(suffixTokens, sql)}`, statement.line, index.objectId); lossReport.preservedOpaque.push(`${index.name}: ${raw(suffixTokens, sql)}`); }
      if (suffixTokens.length && !hasPredicate && !indexUnsupportedOptions) issue("DDL_INDEX_STORAGE_REVIEW", "Bounded dialect index options are retained as SQL text; physical-storage semantics require target review.", statement.line, index.objectId);
      setEngineering(index, {
        indexUnsupportedOptions,
        indexIncludedFieldIds,
        indexPredicate,
        indexRemainingSuffix: raw(indexPredicate ? [] : suffixTokens, sql),
        indexParts,
        clustering: indexClustering,
        indexMethod,
        indexExpressions: expressions,
        indexSuffix: raw(t.slice(body.next), sql),
        dialect,
      });
      document.objects.push(index);
      lossReport.preserved.push(`Index ${index.name}`);
      continue;
    }
    let viewAt = 1;
    while (["OR", "REPLACE", "FORCE", "NOFORCE", "EDITIONABLE", "NONEDITIONABLE"].some(w => keyword(t[viewAt], w))) viewAt++;
    if (keyword(t[0], "CREATE") && keyword(t[viewAt], "VIEW")) {
      const n = readName(t, viewAt + 1);
      if (!n) {
        retain(statement, "Malformed view name.");
        continue;
      }
      let i = n.next;
      const aliases = balanced(t, i);
      if (aliases) i = aliases.next;
      if (
        !keyword(t[i], "AS") ||
        (!keyword(t[i + 1], "SELECT") && !keyword(t[i + 1], "WITH"))
      ) {
        retain(statement, "Unsupported view definition.");
        continue;
      }
      const view = nameObject("view", scopedName(n.parts, statement), rep, dialect);
      view.sql = raw(t.slice(i + 1), sql);
      setEngineering(view, {
        dialect,
        sourceLine: statement.line,
        viewPrefix: raw(t.slice(1, viewAt), sql).replace(/^OR\s+REPLACE\s*/i, ""),
        viewColumns: aliases
          ? splitTopLevel(aliases.inside).map((p) => normalizePart({ name: p[0]?.value, quoted: !!p[0]?.quoted }, dialect))
          : [],
      });
      document.objects.push(view);
      lossReport.preserved.push(`View ${view.name}`);
      continue;
    }
    if (keyword(t[0], "CREATE") && keyword(t[1], "SEQUENCE")) {
      const n = readName(t, 2);
      if (!n) {
        retain(statement, "Malformed sequence.");
        continue;
      }
      const sequence = nameObject("sequence", scopedName(n.parts, statement), rep, dialect);
      setEngineering(sequence, {
        dialect,
        sequenceOptions: raw(t.slice(n.next), sql),
      });
      document.objects.push(sequence);
      lossReport.preserved.push(`Sequence ${sequence.name}`);
      continue;
    }
    if (keyword(t[0], "CREATE") && keyword(t[1], "SCHEMA")) {
      const n = readName(t, 2);
      if (n && n.next === t.length) {
        const list = (document.extensions.ddlNamespaces ?? []) as string[];
        list.push(n.parts.map((p) => normalizePart(p, dialect)).join("."));
        document.extensions.ddlNamespaces = list;
        continue;
      }
    }
    if (keyword(t[0], "ALTER") && keyword(t[1], "SEQUENCE")) {
      const n = readName(t, 2), seq = n && document.objects.find(o => o.kind === "sequence" && nameKey((engineering(o).identifier ?? []), dialect) === nameKey(scopedName(n.parts, statement), dialect));
      if (seq && n && keyword(t[n.next], "OWNED") && keyword(t[n.next + 1], "BY")) {
        const cn = readName(t, n.next + 2);
        if (cn && cn.parts.length >= 2) { const table = findTable(scopedName(cn.parts.slice(0, -1), statement)), col = table && findColumn(table, cn.parts.at(-1)!); if (col && cn.next === t.length) { setEngineering(seq, { ownedByColumnId: col.objectId }); continue; } }
        if (keyword(t[n.next + 2], "NONE")) { setEngineering(seq, { ownedByNone: true }); continue; }
      }
      retain(statement, "ALTER SEQUENCE is source-only unless its OWNED BY target resolves."); continue;
    }
    if (keyword(t[0], "COMMENT") && keyword(t[1], "ON") && ["TABLE", "COLUMN"].some(w => keyword(t[2], w))) {
      const n = readName(t, 3);
      const tableParts = n && (keyword(t[2], "COLUMN") ? n.parts.slice(0, -1) : n.parts);
      const table = tableParts && findTable(scopedName(tableParts, statement));
      const target = table && n && (keyword(t[2], "COLUMN") ? findColumn(table, n.parts.at(-1)!) : table);
      if (target && n && keyword(t[n.next], "IS") && t[n.next + 1]?.kind === "string") { target.description = t[n.next + 1].value; continue; }
    }
    retain(
      statement,
      keyword(t[0], "CREATE") && ["DOMAIN", "TYPE", "MATERIALIZED", "SYNONYM", "RULE", "DEFAULT"].some(w => keyword(t[1], w))
        ? `${t[1].value.toUpperCase()} definitions are source-only; their dependent object semantics are not modeled or generated.`
        : "Statement is not in the implemented structural parser subset; source retained without execution.",
    );
  }
  if (dialect === "ase") for (const statement of parsed.statements) {
    diagnosticStatement = statement;
    const t = statement.tokens;
    if (!["EXEC", "EXECUTE"].some(w => keyword(t[0], w)) || !["SP_BINDEFAULT", "SP_BINDRULE"].some(w => keyword(t[1], w.toUpperCase()))) continue;
    const target = t.filter(token => token.kind === "string").at(-1)?.value;
    if (!target) continue;
    const name = readName(tokenize(target, dialect).tokens);
    const table = name && name.parts.length >= 2 && findTable(name.parts.slice(0, -1));
    const column = table && name && findColumn(table, name.parts.at(-1)!);
    if (column) {
      setEngineering(column, { sourceOnlyBindings: [...(engineering(column).sourceOnlyBindings as string[] ?? []), statement.text] });
      issue("DDL_BINDING_SOURCE_ONLY", `Bound rule/default for ${target} is source-only. Its effective default/validation behavior is unresolved.`, statement.line, column.objectId);
      lossReport.unresolved.push(`${target}: source-only binding`);
    }
  }
  for (const col of document.objects.filter(o => o.kind === "column" && o.defaultValue)) {
    const tokens = tokenize(col.defaultValue!, dialect).tokens;
    let sequenceName: NamePart[] | undefined;
    if (dialect === "postgresql") {
      const at = tokens.findIndex(t => keyword(t, "NEXTVAL"));
      if (at >= 0 && tokens[at + 2]?.kind === "string") sequenceName = readName(tokenize(tokens[at + 2].value, dialect).tokens)?.parts;
    } else if (dialect === "oracle") {
      const name = readName(tokens);
      if (name?.parts.at(-1)?.name.toUpperCase() === "NEXTVAL") sequenceName = name.parts.slice(0, -1);
    }
    if (!sequenceName) continue;
    const sequences = document.objects.filter(o => o.kind === "sequence" && (nameKey(engineering(o).identifier ?? [], dialect) === nameKey(sequenceName!, dialect) || (sequenceName!.length === 1 && nameKey([{name:o.name,quoted:!!engineering(o).identifier?.at(-1)?.quoted}],dialect) === nameKey(sequenceName!,dialect))));
    if (sequences.length === 1) setEngineering(col, { defaultSequenceId: sequences[0].objectId });
    else {
      setEngineering(col, { sourceOnlySequence: sequenceName.map(p => p.name).join(".") });
      issue("DDL_SEQUENCE_DEPENDENCY", `Default for ${col.name} references an unresolved sequence. The sequence prerequisite must be modeled before generating this table.`, undefined, col.objectId);
      lossReport.unresolved.push(`${col.name}: unresolved sequence default`);
    }
  }
  document.extensions.ddlOpaque = opaque;
  document.extensions.ddlImport = {
    dialect,
    statementCount: parsed.statements.length,
    parserVersion: "0.2.0-alpha.0",
    execution: "never",
    coverage: "structural-subset",
  };
  document.diagrams[0].appearances = document.objects
    .filter(
      (o) => !o.ownerObjectId && ["table", "view", "sequence"].includes(o.kind),
    )
    .map((o, i) => ({
      appearanceId: uid(),
      objectId: o.objectId,
      position: { x: 80 + (i % 4) * 330, y: 80 + Math.floor(i / 4) * 280 },
    }));
  document.diagrams[0].connectors = document.relationships.map((r) => ({
    relationshipId: r.relationshipId,
    routing: "auto",
  }));
  syncDocument(document);
  diagnostics.push(...validateModel(document));
  for (const diagnostic of diagnostics) {
    if (!diagnostic.objectId || diagnostic.sourceStart !== undefined) continue;
    const object = document.objects.find(o => o.objectId === diagnostic.objectId), relationship = document.relationships.find(r => r.relationshipId === diagnostic.objectId);
    const eng = object ? engineering(object) : relationship?.extensions?.engineering as Eng | undefined;
    if (eng?.sourceStart !== undefined) { diagnostic.sourceStart = Number(eng.sourceStart); diagnostic.sourceEnd = Number(eng.sourceEnd); diagnostic.line ??= Number(eng.sourceLine); }
  }
  issue(
      "DIALECT_EXECUTION_UNVERIFIED",
      `${dialect} structural import is available; server execution and round-trip certification are unverified.`,
      undefined,
      undefined,
      "info",
    );
  return { document, diagnostics, lossReport };
}
function renderType(
  o: DomainObject,
  dialect: Dialect,
  diagnostics: Diagnostic[],
  loss: LossReport,
): string | undefined {
  const spec = o.type;
  if (!spec) {
    diagnostics.push({
      code: "DDL_TYPE_REQUIRED",
      severity: "error",
      objectId: o.objectId,
      message: `Column ${o.name} needs a physical datatype.`,
    });
    return;
  }
  const eng = engineering(o),
    origin = eng.typeDialect,
    original = eng.originalType as TypeSpec | undefined;
  const fieldsChanged =
    original &&
    (["name", "length", "precision", "scale"] as const).some(
      (k) => spec[k] !== original[k],
    );
  const dimensions =
    spec.precision !== undefined
      ? "(" +
        spec.precision +
        (spec.scale !== undefined ? "," + spec.scale : "") +
        ")"
      : spec.length !== undefined
        ? "(" + spec.length + (eng.typeLengthSemantics ? " " + eng.typeLengthSemantics : "") + ")"
        : "";
  const temporal = spec.name.match(
    /^(timestamp|time)( with(?:out)? time zone)$/i,
  );
  const edited = temporal
    ? temporal[1] + dimensions + temporal[2]
    : spec.name + dimensions;
  const source = fieldsChanged || !spec.raw ? edited : spec.raw;
  if (origin === dialect) {
    if (
      !/^[\p{L}\p{N}_\s.,+*\-()[\]"`]+$/u.test(source) ||
      !expressionSafe(source, dialect)
    ) {
      diagnostics.push({
        code: "DDL_UNSAFE_TYPE",
        severity: "error",
        objectId: o.objectId,
        message: "Datatype contains unsupported syntax.",
      });
      return;
    }
    return source;
  }
  const name = spec.name.toLowerCase().trim(),
    arg = spec.length ?? 255;
  let target: string | undefined;
  if (
    /^(?:varchar2?|nvarchar2?|character varying|char varying|string)$/.test(
      name,
    )
  )
    target = `${dialect === "oracle" ? "VARCHAR2" : dialect === "sqlserver" ? "NVARCHAR" : "VARCHAR"}(${arg})`;
  else if (/^(?:text|longtext|clob|nclob)$/.test(name))
    target =
      dialect === "oracle"
        ? "CLOB"
        : dialect === "sqlserver"
          ? "NVARCHAR(MAX)"
          : dialect === "mysql"
            ? "LONGTEXT"
            : "TEXT";
  else if (/^(?:char|nchar|character)$/.test(name))
    target = `CHAR(${spec.length ?? 1})`;
  else if (/^(?:integer|int|int4|smallint|int2|bigint|int8)$/.test(name)) {
    const big = /big|int8/.test(name),
      small = /small|int2/.test(name);
    target =
      dialect === "oracle"
        ? `NUMBER(${big ? 19 : small ? 5 : 10})`
        : big
          ? "BIGINT"
          : small
            ? "SMALLINT"
            : "INTEGER";
  } else if (
    name === "number" &&
    dialect === "oracle" &&
    spec.precision === undefined &&
    spec.scale === undefined
  )
    target = "NUMBER";
  else if (
    /^(?:numeric|decimal)$/.test(name) &&
    dialect === "postgresql" &&
    spec.precision === undefined &&
    spec.scale === undefined
  )
    target = name.toUpperCase();
  else if (/^(?:numeric|decimal|number|money)$/.test(name))
    target = `${dialect === "oracle" ? "NUMBER" : "DECIMAL"}(${spec.precision ?? 20},${spec.scale ?? (name === "money" ? 4 : 0)})`;
  else if (
    name === "bit" &&
    !origin &&
    (dialect === "postgresql" || dialect === "mysql")
  )
    target = "BIT(" + String(spec.length ?? 1) + ")";
  else if (
    /^(?:bool|boolean)$/.test(name) ||
    (name === "bit" && (!origin || origin === "sqlserver" || origin === "ase"))
  )
    target =
      dialect === "postgresql"
        ? "BOOLEAN"
        : dialect === "oracle"
          ? "NUMBER(1)"
          : dialect === "sqlserver" || dialect === "ase"
            ? "BIT"
            : "BOOLEAN";
  else if (name === "date") target = "DATE";
  else if (
    /^(?:timestamptz|timestamp with time zone|datetimeoffset)$/.test(name)
  )
    target =
      dialect === "postgresql"
        ? "TIMESTAMPTZ"
        : dialect === "oracle"
          ? "TIMESTAMP WITH TIME ZONE"
          : dialect === "sqlserver"
            ? "DATETIMEOFFSET"
            : undefined;
  else if (
    /^(?:timestamp|datetime|datetime2|timestamp without time zone)$/.test(
      name,
    ) &&
    !(origin === "sqlserver" && name === "timestamp")
  )
    target =
      dialect === "sqlserver"
        ? "DATETIME2"
        : dialect === "ase"
          ? "DATETIME"
          : dialect === "mysql"
            ? "DATETIME"
            : "TIMESTAMP";
  else if (/^(?:uuid|uniqueidentifier)$/.test(name))
    target =
      dialect === "postgresql"
        ? "UUID"
        : dialect === "sqlserver"
          ? "UNIQUEIDENTIFIER"
          : `CHAR(36)`;
  else if (/^(?:float|double precision|double|real)$/.test(name))
    target =
      dialect === "oracle"
        ? "BINARY_DOUBLE"
        : dialect === "postgresql"
          ? "DOUBLE PRECISION"
          : "FLOAT";
  else if (name === "json" || name === "jsonb")
    target =
      dialect === "postgresql"
        ? name.toUpperCase()
        : dialect === "mysql"
          ? "JSON"
          : undefined;
  else if (/^(?:binary|varbinary|bytea|raw)$/.test(name))
    target =
      dialect === "postgresql"
        ? "BYTEA"
        : dialect === "oracle"
          ? `RAW(${spec.length ?? 16})`
          : `VARBINARY(${spec.length ?? 16})`;
  if (!target) {
    diagnostics.push({
      code: "DDL_TYPE_MAPPING_UNRESOLVED",
      severity: "error",
      objectId: o.objectId,
      message: `No reviewed ${dialect} mapping for ${source}.`,
    });
    loss.unresolved.push(`${o.name}: ${source} to ${dialect}`);
    return;
  }
  if (origin && origin !== dialect) {
    diagnostics.push({
      code: "DDL_TYPE_TRANSLATED",
      severity: "warning",
      objectId: o.objectId,
      message: `Review ${source} → ${target}, including range, collation and client behavior.`,
    });
    loss.translated.push(`${o.name}: ${source} → ${target}`);
  }
  return target;
}
export function generateDDL(
  document: ModelDocument,
  dialect: Dialect,
): { sql: string; diagnostics: Diagnostic[]; lossReport: LossReport } {
  const diagnostics: Diagnostic[] = validateModel(document),
    lossReport = emptyLoss(),
    statements: string[] = [];
  const error = (code: string, message: string, objectId?: string) =>
    diagnostics.push({
      code,
      severity: "error",
      message,
      objectId,
      helpTopic: "ddl-generation",
    });
  const warn = (code: string, message: string, objectId?: string) =>
    diagnostics.push({
      code,
      severity: "warning",
      message,
      objectId,
      helpTopic: "ddl-generation",
    });
  if (
    document.model.family !== "relational" ||
    document.model.modelType === "conceptual" ||
    document.model.modelType === "logical"
  ) {
    error(
      "DDL_PHYSICAL_REQUIRED",
      "Derive and review a physical representation before generating DDL.",
    );
    return { sql: "", diagnostics, lossReport };
  }
  const active = document.representations.find(
      (r) => r.representationId === document.model.activeRepresentation,
    ),
    physical =
      active?.kind === "physical"
        ? active
        : document.representations.find((r) => r.kind === "physical");
  if (!physical) {
    error("DDL_PHYSICAL_REQUIRED", "No physical representation is available.");
    return { sql: "", diagnostics, lossReport };
  }
  const objects = document.objects.filter(
      (o) =>
        o.representationId === physical.representationId &&
        o.lifecycle !== "archived",
    ),
    byId = new Map(objects.map((o) => [o.objectId, o])),
    tables = objects
      .filter((o) => o.kind === "table")
      .sort((a, b) =>
        qualified(a, dialect).localeCompare(qualified(b, dialect)),
      );
  if (dialect === "postgresql")
    for (const object of objects)
      if (new TextEncoder().encode(object.name).length > 63)
        error(
          "DDL_IDENTIFIER_TRUNCATION",
          "PostgreSQL identifiers over 63 UTF-8 bytes require an explicit naming decision.",
          object.objectId,
        );
  const columns = (table: DomainObject) =>
    objects
      .filter((o) => o.ownerObjectId === table.objectId && o.kind === "column")
      .sort(
        (a, b) =>
          (a.ordinal ?? 0) - (b.ordinal ?? 0) || a.name.localeCompare(b.name),
      );
  const safeExpr = (value: string, id: string) => {
    if (!value.trim() || !expressionSafe(value, dialect)) {
      error(
        "DDL_EXPRESSION_UNSAFE",
        "SQL fragments must be balanced and contain no script terminators.",
        id,
      );
      return false;
    }
    return true;
  };
  const names = (ids: string[] | undefined, owner: string, id: string) => {
    if (
      !ids?.length ||
      ids.some(
        (x) =>
          byId.get(x)?.ownerObjectId !== owner ||
          byId.get(x)?.kind !== "column",
      )
    ) {
      error(
        "DDL_MEMBER_REFERENCE",
        "Ordered key or relationship members are unresolved.",
        id,
      );
      return undefined;
    }
    return ids
      .map((x) => quoteIdentifier(byId.get(x)!.name, dialect))
      .join(", ");
  };
  const stateSuffix = (eng: Eng, id: string, kind: "key" | "check" | "fk") => {
    const state = eng.constraintState as ConstraintState | undefined;
    if (!state || !Object.keys(state).length) return "";
    if (eng.dialect && eng.dialect !== dialect) { error("DDL_CONSTRAINT_STATE_MAPPING", "Constraint states require an explicit target-dialect review.", id); return ""; }
    const parts: string[] = [];
    if (dialect === "oracle" || dialect === "postgresql") {
      if (state.deferrable !== undefined) parts.push(state.deferrable ? "DEFERRABLE" : "NOT DEFERRABLE");
      if (state.initiallyDeferred !== undefined) parts.push(state.initiallyDeferred ? "INITIALLY DEFERRED" : "INITIALLY IMMEDIATE");
    } else if (state.deferrable !== undefined || state.initiallyDeferred !== undefined) error("DDL_CONSTRAINT_STATE_UNSUPPORTED", "Deferral is unsupported for this target.", id);
    if (dialect === "oracle") {
      if (state.enabled !== undefined) parts.push(state.enabled ? "ENABLE" : "DISABLE");
      if (state.validated !== undefined) parts.push(state.validated ? "VALIDATE" : "NOVALIDATE");
    } else if (dialect === "postgresql") {
      if (state.enabled === false || (kind === "key" && state.validated === false)) error("DDL_CONSTRAINT_STATE_UNSUPPORTED", "This PostgreSQL constraint state cannot be emitted faithfully.", id);
      if (state.validated === false && kind !== "key") parts.push("NOT VALID");
    } else if (dialect !== "sqlserver" && (state.enabled !== undefined || state.validated !== undefined)) error("DDL_CONSTRAINT_STATE_UNSUPPORTED", "Constraint enforcement state is unsupported for this target.", id);
    return parts.length ? " " + parts.join(" ") : "";
  };
  const postTable: string[] = [];
  const namespaces = new Set<string>([
    ...((document.extensions.ddlNamespaces ?? []) as string[]),
    ...tables.flatMap((t) => (t.schema ? [t.schema] : [])),
  ]);
  for (const schema of [...namespaces].sort()) {
    if (
      (schema === "public" && dialect === "postgresql") ||
      (schema === "dbo" && dialect === "sqlserver")
    )
      continue;
    if (dialect === "postgresql" || dialect === "sqlserver")
      statements.push(`CREATE SCHEMA ${quoteIdentifier(schema, dialect)};`);
    else if (dialect === "mysql")
      warn(
        "DDL_NAMESPACE_PRECONDITION",
        `Database ${schema} must already exist; creation and credentials are separate.`,
      );
    else
      warn(
        "DDL_NAMESPACE_PRECONDITION",
        `Owner/schema ${schema} must already exist; no users or credentials are generated.`,
      );
  }
  for (const seq of objects
    .filter((o) => o.kind === "sequence")
    .sort((a, b) => a.name.localeCompare(b.name))) {
    if (dialect === "mysql" || dialect === "ase") {
      error(
        "DDL_SEQUENCE_UNSUPPORTED",
        `Independent sequence generation is unverified for ${dialect}.`,
        seq.objectId,
      );
      continue;
    }
    const eng = engineering(seq),
      options = String(eng.sequenceOptions ?? "");
    if (eng.dialect && eng.dialect !== dialect && options) {
      error(
        "DDL_SEQUENCE_MAPPING",
        "Sequence options require a reviewed target mapping.",
        seq.objectId,
      );
      continue;
    }
    if (!/^[A-Za-z0-9_\s+-]*$/.test(options)) {
      error(
        "DDL_SEQUENCE_OPTIONS",
        "Unsupported sequence option syntax.",
        seq.objectId,
      );
      continue;
    }
    statements.push(
      `CREATE SEQUENCE ${qualified(seq, dialect)}${options ? " " + options : ""};`,
    );
  }
  for (const table of tables) {
    const defs: string[] = [],
      cols = columns(table);
    if (engineering(table).opaqueClauses?.some(clause => /\bINHERITS\s*\(|\bPARTITION\s+OF\b/i.test(clause))) { error("DDL_SOURCE_DEPENDENCY_UNRESOLVED", `Table ${table.name} depends on unmodeled inheritance/partition structure. Exclude it or explicitly model the required columns before generation.`, table.objectId); continue; }
    if (!cols.length) {
      error(
        "DDL_EMPTY_TABLE",
        `Table ${table.name} has no columns.`,
        table.objectId,
      );
      continue;
    }
    for (const col of cols) {
      const eng = engineering(col);
      if (eng.sourceOnlyType || eng.sourceOnlyBindings || eng.sourceOnlySequence || (eng.defaultSequenceId && !byId.has(String(eng.defaultSequenceId)))) { error("DDL_SOURCE_DEPENDENCY_UNRESOLVED", `Column ${col.name} depends on source-only datatype/default/rule semantics. Exclude its table or model those prerequisites before generation.`, col.objectId); continue; }
      if (dialect === "sqlserver" && eng.generationKind === "computed" && col.type) { error("DDL_COMPUTED_DECLARED_TYPE", "SQL Server computed column types are inferred from the expression. Use an explicit CAST/CONVERT expression instead of assigning a declared datatype.", col.objectId); continue; }
      const type = eng.inferredType && !col.type && eng.generationKind === "computed" && eng.dialect === dialect ? "" : renderType(col, dialect, diagnostics, lossReport);
      if (type === undefined) continue;
      let definition = `${quoteIdentifier(col.name, dialect)}${type ? " " + type : ""}`;
      if (eng.columnClause) {
        if (eng.dialect !== dialect) {
          error(
            "DDL_GENERATED_MAPPING",
            "Generated/identity expression requires a reviewed cross-dialect mapping.",
            col.objectId,
          );
        } else if (safeExpr(eng.columnClause, col.objectId))
          definition += " " + eng.columnClause;
      } else if (col.generated && eng.generationKind !== "serial") {
        if (dialect === "postgresql")
          definition += " GENERATED BY DEFAULT AS IDENTITY";
        else if (dialect === "oracle")
          definition += " GENERATED BY DEFAULT AS IDENTITY";
        else if (dialect === "mysql") definition += " AUTO_INCREMENT";
        else definition += " IDENTITY(1,1)";
      }
      if (
        col.defaultValue !== undefined &&
        col.defaultValue !== "" &&
        safeExpr(col.defaultValue, col.objectId)
      ) {
        if (
          engineering(col).typeDialect &&
          engineering(col).typeDialect !== dialect
        ) {
          error(
            "DDL_DEFAULT_MAPPING",
            "Imported default expressions require review before cross-dialect generation.",
            col.objectId,
          );
        } else
          definition +=
            (eng.defaultConstraintName && dialect === "sqlserver"
              ? " CONSTRAINT " +
                quoteIdentifier(String(eng.defaultConstraintName), dialect)
              : "") +
            (eng.defaultOnNull && dialect === "oracle" ? " DEFAULT ON NULL " : " DEFAULT ") +
            col.defaultValue;
      }
      if (col.nullable === false && (eng.generationKind !== "computed" || eng.explicitNotNull)) definition += " NOT NULL";
      else if (dialect === "ase" && eng.generationKind !== "computed") definition += " NULL";
      defs.push(definition);
      for (const clause of eng.opaqueClauses ?? []) {
        lossReport.lost.push(`${table.name}.${col.name}: ${clause}`);
        warn(
          "DDL_OPAQUE_NOT_EMITTED",
          `Unsupported column property omitted: ${clause}`,
          col.objectId,
        );
      }
    }
    const primaryIds = new Set(getSelectedPrimaryKeys(document, table.objectId).map((key) => key.objectId));
    for (const key of objects
      .filter((o) => o.ownerObjectId === table.objectId && o.kind === "key")
      .sort(
        (a, b) =>
          Number(primaryIds.has(b.objectId)) -
            Number(primaryIds.has(a.objectId)) ||
          a.name.localeCompare(b.name),
      )) {
      const fields = names(key.members, table.objectId, key.objectId);
      const eng = engineering(key), directions = eng.memberDirections as string[] | undefined;
      const orderedFields = fields && key.members!.map((id, i) => quoteIdentifier(byId.get(id)!.name, dialect) + (directions?.[i] ? " " + directions[i] : "")).join(", ");
      if ((eng.clustering || directions?.some(Boolean) || eng.nullsNotDistinct) && eng.dialect !== dialect) error("DDL_KEY_OPTIONS_MAPPING", "Key ordering/clustering/null equality needs target review.", key.objectId);
      if (fields) defs.push(`CONSTRAINT ${quoteIdentifier(key.name, dialect)} ${primaryIds.has(key.objectId) ? "PRIMARY KEY" : "UNIQUE"}${eng.clustering ? " " + eng.clustering : ""}${eng.nullsNotDistinct ? " NULLS NOT DISTINCT" : ""} (${orderedFields})${stateSuffix(eng, key.objectId, "key")}`);
      for (const clause of engineering(key).opaqueClauses ?? []) {
        lossReport.lost.push(`${key.name}: ${clause}`);
        warn(
          "DDL_OPAQUE_NOT_EMITTED",
          "Key options are not emitted.",
          key.objectId,
        );
      }
    }
    for (const constraint of objects.filter(
      (o) => o.ownerObjectId === table.objectId && o.kind === "constraint",
    )) {
      if (!constraint.checkExpression) {
        error(
          "DDL_CONSTRAINT_UNSUPPORTED",
          "Only explicitly modeled CHECK constraints can be rendered.",
          constraint.objectId,
        );
        continue;
      }
      if (
        engineering(constraint).dialect &&
        engineering(constraint).dialect !== dialect
      ) {
        error(
          "DDL_CHECK_MAPPING",
          "Imported CHECK expression needs target review.",
          constraint.objectId,
        );
        continue;
      }
      if (safeExpr(constraint.checkExpression, constraint.objectId)) {
        const eng = engineering(constraint), state = eng.constraintState as ConstraintState | undefined;
        const definition = `CONSTRAINT ${quoteIdentifier(constraint.name, dialect)} CHECK (${constraint.checkExpression})${stateSuffix(eng, constraint.objectId, "check")}`;
        if ((dialect === "sqlserver" && state) || (dialect === "postgresql" && state?.validated === false)) {
          postTable.push(`ALTER TABLE ${qualified(table, dialect)}${dialect === "sqlserver" ? state?.validated === false ? " WITH NOCHECK" : " WITH CHECK" : ""} ADD ${definition};`);
          if (dialect === "sqlserver" && state?.enabled === false) postTable.push(`ALTER TABLE ${qualified(table, dialect)} NOCHECK CONSTRAINT ${quoteIdentifier(constraint.name, dialect)};`);
        } else defs.push(definition);
      }
    }
    statements.push(
      `CREATE TABLE ${qualified(table, dialect)} (\n  ${defs.join(",\n  ")}\n);`,
    );
    lossReport.preserved.push(`Table ${table.name}`);
    for (const clause of engineering(table).opaqueClauses ?? []) {
      lossReport.lost.push(`${table.name}: ${clause}`);
      warn(
        "DDL_OPAQUE_NOT_EMITTED",
        `Table options are not emitted: ${clause}`,
        table.objectId,
      );
    }
  }
  statements.push(...postTable);
  for (const rel of document.relationships
    .filter((r) => r.representationId === physical.representationId)
    .sort((a, b) => a.name.localeCompare(b.name))) {
    const from = byId.get(rel.sourceObjectId),
      to = byId.get(rel.targetObjectId);
    if (!from || !to || from.kind !== "table" || to.kind !== "table") {
      error(
        "DDL_RELATIONSHIP_END",
        "Physical relationship endpoints must be tables.",
        rel.relationshipId,
      );
      continue;
    }
    const source = names(
        rel.sourceAttributeIds,
        from.objectId,
        rel.relationshipId,
      ),
      target = names(rel.targetAttributeIds, to.objectId, rel.relationshipId);
    if (
      !source ||
      !target ||
      rel.sourceAttributeIds!.length !== rel.targetAttributeIds!.length
    ) {
      error(
        "DDL_FK_ARITY",
        "Foreign-key member counts must match.",
        rel.relationshipId,
      );
      continue;
    }
    if (
      !objects.some(
        (o) =>
          o.kind === "key" &&
          o.ownerObjectId === to.objectId &&
          o.members?.length === rel.targetAttributeIds!.length &&
          o.members.every((m, i) => dialect === "postgresql" ? rel.targetAttributeIds!.includes(m) : m === rel.targetAttributeIds![i]),
      )
    ) {
      error(
        "DDL_FK_TARGET_KEY",
        "Referenced columns need a modeled primary or unique key.",
        rel.relationshipId,
      );
      continue;
    }
    const relEng = rel.extensions?.engineering as Eng ?? {}, relState = relEng.constraintState as ConstraintState | undefined;
    let stmt = `ALTER TABLE ${qualified(from, dialect)}${dialect === "sqlserver" && relState ? relState.validated === false ? " WITH NOCHECK" : " WITH CHECK" : ""} ADD CONSTRAINT ${quoteIdentifier(rel.name, dialect)} FOREIGN KEY (${source}) REFERENCES ${qualified(to, dialect)} (${target})`;
    if (relState?.match) { if (dialect === "postgresql") stmt += " MATCH " + relState.match; else error("DDL_MATCH_MAPPING", "MATCH mode requires target review.", rel.relationshipId); }
    for (const [operation, action] of [
      ["DELETE", rel.onDelete],
      ["UPDATE", rel.onUpdate],
    ]) {
      if (!action) continue;
      if (
        ![
          "CASCADE",
          "RESTRICT",
          "SET NULL",
          "SET DEFAULT",
          "NO ACTION",
        ].includes(action)
      ) {
        error(
          "DDL_FK_ACTION",
          "Invalid referential action.",
          rel.relationshipId,
        );
        continue;
      }
      if (
        dialect === "oracle" &&
        (operation === "UPDATE" ||
          !["CASCADE", "SET NULL", "NO ACTION"].includes(action))
      ) {
        error(
          "DDL_FK_ACTION_UNSUPPORTED",
          `Oracle target does not emit ON ${operation} ${action}.`,
          rel.relationshipId,
        );
        continue;
      }
      if (dialect === "oracle" && action === "NO ACTION") continue;
      if (dialect === "mysql" && action === "SET DEFAULT") {
        error(
          "DDL_FK_ACTION_UNSUPPORTED",
          "MySQL SET DEFAULT is not in the supported enforcement profile.",
          rel.relationshipId,
        );
        continue;
      }
      stmt += ` ON ${operation} ${action}`;
    }
    statements.push(stmt + stateSuffix(relEng, rel.relationshipId, "fk") + ";");
    if (dialect === "sqlserver" && relState?.enabled === false) statements.push(`ALTER TABLE ${qualified(from, dialect)} NOCHECK CONSTRAINT ${quoteIdentifier(rel.name, dialect)};`);
    if (rel.extensions?.opaqueClauses) {
      lossReport.lost.push(
        `${rel.name}: ${String(rel.extensions.opaqueClauses)}`,
      );
      warn(
        "DDL_OPAQUE_NOT_EMITTED",
        "Foreign-key options are omitted.",
        rel.relationshipId,
      );
    }
  }
  for (const index of objects
    .filter((o) => o.kind === "index")
    .sort((a, b) => a.name.localeCompare(b.name))) {
    const table = byId.get(index.ownerObjectId ?? "");
    if (!table) {
      error("DDL_INDEX_OWNER", "Index owner is unresolved.", index.objectId);
      continue;
    }
    const eng = engineering(index);
    if (eng.indexUnsupportedOptions) { error("DDL_INDEX_OPTIONS_UNRESOLVED", "Index suffix contains unsupported semantics. Repair the source or exclude its table before generation.", index.objectId); continue; }
    let expressions: string | undefined;
    if (Array.isArray(eng.indexParts) && eng.dialect === dialect)
      expressions = (
        eng.indexParts as {
          fieldId?: string;
          suffix?: string;
          expression?: string;
        }[]
      )
        .map((p) => {
          if (p.fieldId) {
            const col = byId.get(p.fieldId);
            if (!col || col.ownerObjectId !== table.objectId) {
              error(
                "DDL_INDEX_MEMBER",
                "Index member is unresolved.",
                index.objectId,
              );
              return "";
            }
            return (
              quoteIdentifier(col.name, dialect) +
              (p.suffix ? " " + p.suffix : "")
            );
          }
          warn(
            "DDL_EXPRESSION_DEPENDENCIES_UNVERIFIED",
            "Expression index dependencies require target validation after field renames.",
            index.objectId,
          );
          return p.expression ?? "";
        })
        .join(", ");
    else if (eng.indexExpressions && eng.dialect === dialect)
      expressions = eng.indexExpressions.join(", ");
    else if (index.expression) {
      if (eng.dialect && eng.dialect !== dialect) {
        error(
          "DDL_INDEX_MAPPING",
          "Index expression requires target review.",
          index.objectId,
        );
        continue;
      }
      expressions = index.expression;
    } else expressions = names(index.members, table.objectId, index.objectId);
    if (!expressions) { error("DDL_INDEX_MEMBERS", "Index requires at least one resolved field or expression.", index.objectId); continue; }
    if (!safeExpr(expressions, index.objectId)) continue;
    let suffix = eng.indexSuffix ?? "";
    if (Array.isArray(eng.indexIncludedFieldIds) || eng.indexPredicate !== undefined) {
      const included = (eng.indexIncludedFieldIds as string[] | undefined)?.length ? names(eng.indexIncludedFieldIds as string[], table.objectId, index.objectId) : undefined;
      suffix = [included ? `INCLUDE (${included})` : "", eng.indexPredicate ? "WHERE " + eng.indexPredicate : "", eng.indexRemainingSuffix ?? ""].filter(Boolean).join(" ");
    }
    if ((eng.clustering || eng.indexMethod) && eng.dialect !== dialect) { error("DDL_INDEX_MAPPING", "Index clustering/access method needs reviewed target mapping.", index.objectId); continue; }
    if (suffix && eng.dialect !== dialect) {
      error(
        "DDL_INDEX_MAPPING",
        "Index options require target review.",
        index.objectId,
      );
      continue;
    }
    if (suffix && !safeExpr(suffix, index.objectId)) continue;
    statements.push(
      `CREATE ${index.unique ? "UNIQUE " : ""}${eng.clustering ? eng.clustering + " " : ""}INDEX ${quoteIdentifier(index.name, dialect)} ON ${qualified(table, dialect)}${eng.indexMethod ? " USING " + eng.indexMethod : ""} (${expressions})${suffix ? " " + suffix : ""};`,
    );
  }
  // Views are retained query text, never advertised as cross-dialect translated queries.
  for (const view of objects
    .filter((o) => o.kind === "view")
    .sort(
      (a, b) =>
        Number(engineering(a).sourceLine ?? Number.MAX_SAFE_INTEGER) -
          Number(engineering(b).sourceLine ?? Number.MAX_SAFE_INTEGER) ||
        a.name.localeCompare(b.name),
    )) {
    if (
      !view.sql ||
      !/^\s*(SELECT|WITH)\b/i.test(view.sql) ||
      !safeExpr(view.sql, view.objectId)
    ) {
      error(
        "DDL_VIEW_QUERY",
        "A view needs one bounded SELECT/WITH definition.",
        view.objectId,
      );
      continue;
    }
    if (engineering(view).dialect && engineering(view).dialect !== dialect) {
      error(
        "DDL_VIEW_MAPPING",
        "View query requires reviewed target-specific translation.",
        view.objectId,
      );
      continue;
    }
    warn(
      "DDL_VIEW_DEPENDENCIES_UNVERIFIED",
      "View query text is preserved in source order; dependency, column-rename and target query validation require the database or a typed query adapter.",
      view.objectId,
    );
    const aliases = engineering(view).viewColumns as string[] | undefined;
    statements.push(
      `CREATE ${engineering(view).viewPrefix ? engineering(view).viewPrefix + " " : ""}VIEW ${qualified(view, dialect)}${aliases?.length ? " (" + aliases.map((n) => quoteIdentifier(n, dialect)).join(", ") + ")" : ""} AS\n${view.sql};`,
    );
  }
  if (dialect === "postgresql") for (const sequence of objects.filter(o => o.kind === "sequence")) {
    const eng = engineering(sequence), col = byId.get(String(eng.ownedByColumnId ?? "")), table = col && byId.get(col.ownerObjectId ?? "");
    if (eng.ownedByColumnId && (!col || !table)) error("DDL_SEQUENCE_OWNER", "Sequence ownership target is unresolved.", sequence.objectId);
    else if (table && col) statements.push(`ALTER SEQUENCE ${qualified(sequence, dialect)} OWNED BY ${qualified(table, dialect)}.${quoteIdentifier(col.name, dialect)};`);
    else if (eng.ownedByNone) statements.push(`ALTER SEQUENCE ${qualified(sequence, dialect)} OWNED BY NONE;`);
  }
  if (dialect === "postgresql" || dialect === "oracle") for (const object of objects.filter(o => o.description && ["table", "column"].includes(o.kind))) {
    const table = object.ownerObjectId && byId.get(object.ownerObjectId);
    statements.push(`COMMENT ON ${object.kind.toUpperCase()} ${table ? qualified(table, dialect) + "." + quoteIdentifier(object.name, dialect) : qualified(object, dialect)} IS '${object.description!.replaceAll("'", "''")}';`);
  }
  for (const entry of (document.extensions.ddlOpaque ?? []) as {
    text: string;
    line: number;
    reason: string;
  }[]) {
    lossReport.lost.push(`Line ${entry.line}: ${entry.reason}`);
    warn(
      "DDL_OPAQUE_NOT_EMITTED",
      `Unsupported source at line ${entry.line} remains in the native model but is omitted from SQL.`,
    );
  }
  diagnostics.push({
      code: "DIALECT_EXECUTION_UNVERIFIED",
      severity: "info",
      message: `${dialect} output has structural fixtures only; no server execution certification is claimed.`,
    });
  // Any error blocks the whole generated executable bundle; preview loss warnings remain explicit.
  return {
    sql: diagnostics.some((d) => d.severity === "error")
      ? ""
      : statements.join(
          dialect === "sqlserver" || dialect === "ase" ? "\nGO\n\n" : "\n\n",
        ) +
        (statements.length
          ? dialect === "sqlserver" || dialect === "ase"
            ? "\nGO\n"
            : "\n"
          : ""),
    diagnostics,
    lossReport,
  };
}
